import { useCallback, useRef, useMemo } from 'react';
import {
  useMeetingSelector,
  useStableEvent
} from '../meeting/meetingProviderContextDef';
import {
  useCharacterTrackedState,
  CHARACTER_STATE_KEYS,
  EMPTY_CHARACTER_STATE
} from './characterStateContext';
import { events } from '../utils';

const NOOP = () => {};

/**
 *
 * @param {
 * {
 *  interactionId?: string,
 *  id?: string,
 *  displayName?: string,
 *  characterRole?: string,
 *  characterMode?: "text" | "co_pilot" | "auto_pilot" | "vision_pilot",
 *  knowledgeBases?: string[],
 *  language?: string,
 * },
 * {
 *  onCharacterMessage?: Function,
 *  onData?: Function,
 *  onError?: Function,
 *  onUserMessage?: Function,
 *  onStreamDisabled?: Function,
 *  onStreamEnabled?: Function,
 *  onMediaStatusChanged?: Function,
 *  onVideoQualityChanged?: Function,
 * }} options
 *
 * @returns {
 *
 *  displayName: string,
 *  webcamStream: Stream,
 *  micStream: Stream,
 *  webcamOn: boolean,
 *  micOn: boolean,
 *  isActiveSpeaker: string,
 *
 *  enableMic: Function,
 *  disableMic: Function,
 *  enableWebcam: Function,
 *  disableWebcam: Function,
 *
 *  join: Function,
 *  leave: Function,
 *  sendMessage: Function
 *  interrupt: Function
 *
 *}
 *
 */
const useCharacter = (
  {
    interactionId,
    id,
    characterMode,
    displayName,
    characterRole,
    knowledgeBases,
    language,
    metaData
  },
  {
    onCharacterStateChanged = NOOP,
    onCharacterMessage = NOOP,
    onUserMessage = NOOP,
    onData = NOOP,
    onError = NOOP,
    onCharacterJoined = NOOP,
    onCharacterLeft = NOOP,
    onStreamEnabled = NOOP,
    onStreamDisabled = NOOP,
    onMediaStatusChanged = NOOP,
    onVideoQualityChanged = NOOP
  } = {}
) => {
  const allState = useCharacterTrackedState();
  const meeting = useMeetingSelector(s => s.meeting);
  const characters = useMeetingSelector(s => s.characters);

  const character = useMemo(() => {
    if (!meeting) return null;
    if (characters) {
      if (interactionId) {
        for (const c of characters.values()) {
          if (c.interactionId == interactionId) return c;
        }
      }
      if (id) {
        const found = characters.get(id);
        if (found) return found;
      }
    }
    if (typeof meeting.createCharacter === 'function') {
      return meeting.createCharacter({
        interactionId,
        displayName,
        id,
        characterRole,
        characterMode,
        knowledgeBases,
        language,
        metaData
      });
    }
    return null;
  }, [
    meeting,
    characters,
    interactionId,
    id,
    characterMode,
    displayName,
    characterRole,
    knowledgeBases,
    language,
    metaData
  ]);

  const characterRef = useRef(character);
  characterRef.current = character;

  const characterId = character?.id;

  useStableEvent(
    `${events['character-state-changed']}-${characterId}`,
    onCharacterStateChanged
  );
  useStableEvent(
    `${events['character-message']}-${characterId}`,
    onCharacterMessage
  );
  useStableEvent(`${events['user-message']}-${characterId}`, onUserMessage);
  useStableEvent(`${events['character-data']}-${characterId}`, onData);
  useStableEvent(`${events['character-error']}-${characterId}`, onError);
  useStableEvent(
    `${events['character-joined']}-${characterId}`,
    onCharacterJoined
  );
  useStableEvent(`${events['character-left']}-${characterId}`, onCharacterLeft);
  useStableEvent(
    `${events['participant-stream-enabled']}-${characterId}`,
    onStreamEnabled
  );
  useStableEvent(
    `${events['participant-stream-disabled']}-${characterId}`,
    onStreamDisabled
  );
  useStableEvent(
    `${events['participant-media-status-changed']}-${characterId}`,
    onMediaStatusChanged
  );
  useStableEvent(
    `${events['participant-video-quality-changed']}-${characterId}`,
    onVideoQualityChanged
  );

  const enableMic = useCallback(() => characterRef.current?.enableMic(), []);
  const disableMic = useCallback(() => characterRef.current?.disableMic(), []);
  const enableWebcam = useCallback(
    () => characterRef.current?.enableWebcam(),
    []
  );
  const disableWebcam = useCallback(
    () => characterRef.current?.disableWebcam(),
    []
  );
  const join = useCallback(async () => {
    await characterRef.current?.join();
  }, []);
  const leave = useCallback(async () => {
    await characterRef.current?.leave();
  }, []);
  const sendMessage = useCallback(async d => {
    await characterRef.current?.sendMessage(d);
  }, []);
  const interrupt = useCallback(async () => {
    await characterRef.current?.interrupt();
  }, []);

  const methodsRef = useRef(null);
  methodsRef.current = {
    enableMic,
    disableMic,
    enableWebcam,
    disableWebcam,
    join,
    leave,
    sendMessage,
    interrupt
  };

  return useMemo(
    () =>
      new Proxy(allState, {
        get(target, prop, receiver) {
          if (prop in CHARACTER_STATE_KEYS) {
            const cState = characterId ? target[characterId] : null;
            return cState ? cState[prop] : EMPTY_CHARACTER_STATE[prop];
          }
          const m = methodsRef.current;
          if (m && prop in m) {
            if (!characterRef.current) {
              return () => {
                console.warn(
                  `useCharacter: ${String(
                    prop
                  )}() called but character not found.`
                );
              };
            }
            return m[prop];
          }
          return Reflect.get(target, prop, receiver);
        },
        has(target, prop) {
          return (
            prop in CHARACTER_STATE_KEYS ||
            !!(methodsRef.current && prop in methodsRef.current)
          );
        },
        ownKeys() {
          const stateKeys = Object.keys(CHARACTER_STATE_KEYS);
          const mKeys = Object.keys(methodsRef.current ?? {});
          return [...new Set([...stateKeys, ...mKeys])];
        },
        getOwnPropertyDescriptor() {
          return { configurable: true, enumerable: true, writable: false };
        }
      }),
    [allState, characterId]
  );
};

export default useCharacter;
